import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { FinancialSummary } from './financialService'
import { SellerPerformance } from './sellerAnalyticsService'

/**
 * Export data to CSV
 */
export function exportToCSV(data: any[], fileName: string, headers?: string[]) {
  try {
    if (data.length === 0) {
      throw new Error('No data to export')
    }

    // Use provided headers or extract from first object
    const headerKeys = headers || Object.keys(data[0])
    
    // Build CSV content
    const escape = (str: any) => {
      if (str === null || str === undefined) return ''
      const s = String(str)
      return '"' + s.replace(/"/g, '""') + '"'
    }
    
    const csvRows = [
      headerKeys.join(','),
      ...data.map(row => 
        headerKeys.map(key => escape(row[key])).join(',')
      )
    ]
    
    const csvContent = csvRows.join('\n')
    
    // Add BOM for Arabic support
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `${fileName}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    return true
  } catch (error) {
    console.error('Error exporting to CSV:', error)
    throw error
  }
}

/**
 * Export data to Excel
 */
export function exportToExcel(
  data: any[], 
  sheetName: string, 
  fileName: string,
  headers?: Record<string, string>
) {
  try {
    if (data.length === 0) {
      throw new Error('No data to export')
    }

    // If headers mapping provided, rename columns
    let exportData = data
    if (headers) {
      exportData = data.map(row => {
        const newRow: any = {}
        Object.keys(headers).forEach(key => {
          newRow[headers[key]] = row[key]
        })
        return newRow
      })
    }

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
    
    // Generate Excel file
    XLSX.writeFile(workbook, `${fileName}.xlsx`)
    
    return true
  } catch (error) {
    console.error('Error exporting to Excel:', error)
    throw error
  }
}

/**
 * Export data to PDF
 */
export function exportToPDF(
  data: any[], 
  title: string, 
  fileName: string,
  headers: string[],
  columnKeys: string[]
) {
  try {
    const doc = new jsPDF()
    
    // Add title
    doc.setFontSize(16)
    doc.text(title, 14, 20)
    
    // Add date
    doc.setFontSize(10)
    doc.text(`تاريخ التقرير: ${new Date().toLocaleDateString('ar-EG')}`, 14, 30)
    
    // Generate table
    autoTable(doc, {
      head: [headers],
      body: data.map(row => columnKeys.map(key => row[key] || '')),
      startY: 40,
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [255, 102, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      }
    })
    
    // Save PDF
    doc.save(`${fileName}.pdf`)
    
    return true
  } catch (error) {
    console.error('Error exporting to PDF:', error)
    throw error
  }
}

/**
 * Generate comprehensive financial report PDF
 */
export function generateFinancialReportPDF(
  financialData: FinancialSummary,
  fileName: string = 'financial_report'
) {
  try {
    const doc = new jsPDF()
    let yPosition = 20
    
    // Title
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text('Financial Report / التقرير المالي', 105, yPosition, { align: 'center' })
    yPosition += 15
    
    // Period
    doc.setFontSize(12)
    doc.setFont('helvetica', 'normal')
    const startDate = financialData.period.start.toLocaleDateString('en-US')
    const endDate = financialData.period.end.toLocaleDateString('en-US')
    doc.text(`Period: ${startDate} - ${endDate}`, 14, yPosition)
    yPosition += 10
    
    // Generation date
    doc.setFontSize(10)
    doc.text(`Generated: ${new Date().toLocaleString('en-US')}`, 14, yPosition)
    yPosition += 15
    
    // Summary Section
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Executive Summary', 14, yPosition)
    yPosition += 10
    
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    
    const summaryData = [
      ['Total Revenue', `$${financialData.totalRevenue.toFixed(2)}`],
      ['Total Commissions', `$${financialData.totalCommissions.toFixed(2)}`],
      ['Total Discounts', `$${financialData.totalDiscounts.toFixed(2)}`],
      ['Wallet Charges', `$${financialData.walletCharges.toFixed(2)}`],
      ['Wallet Withdrawals', `$${financialData.walletWithdrawals.toFixed(2)}`],
      ['Net Profit', `$${financialData.netProfit.toFixed(2)}`]
    ]
    
    autoTable(doc, {
      body: summaryData,
      startY: yPosition,
      theme: 'grid',
      styles: { fontSize: 10 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { halign: 'right', cellWidth: 60 }
      }
    })
    
    yPosition = (doc as any).lastAutoTable.finalY + 15
    
    // Commissions by Category
    if (Object.keys(financialData.commissionsByCategory).length > 0) {
      if (yPosition > 250) {
        doc.addPage()
        yPosition = 20
      }
      
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Commissions by Category', 14, yPosition)
      yPosition += 10
      
      const categoryData = Object.entries(financialData.commissionsByCategory).map(
        ([category, amount]) => [category, `$${amount.toFixed(2)}`]
      )
      
      autoTable(doc, {
        head: [['Category', 'Commission Amount']],
        body: categoryData,
        startY: yPosition,
        theme: 'striped',
        headStyles: {
          fillColor: [255, 102, 0]
        }
      })
      
      yPosition = (doc as any).lastAutoTable.finalY + 15
    }
    
    // Payment Methods Breakdown
    if (Object.keys(financialData.paymentMethodsBreakdown).length > 0) {
      if (yPosition > 250) {
        doc.addPage()
        yPosition = 20
      }
      
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Payment Methods Breakdown', 14, yPosition)
      yPosition += 10
      
      const paymentData = Object.entries(financialData.paymentMethodsBreakdown).map(
        ([method, amount]) => [method, `$${amount.toFixed(2)}`]
      )
      
      autoTable(doc, {
        head: [['Payment Method', 'Amount']],
        body: paymentData,
        startY: yPosition,
        theme: 'striped',
        headStyles: {
          fillColor: [255, 102, 0]
        }
      })
      
      yPosition = (doc as any).lastAutoTable.finalY + 15
    }
    
    // Top Sellers
    if (financialData.topSellers.length > 0) {
      if (yPosition > 220) {
        doc.addPage()
        yPosition = 20
      }
      
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Top 10 Sellers by Revenue', 14, yPosition)
      yPosition += 10
      
      const sellersData = financialData.topSellers.map(seller => [
        seller.name,
        `$${seller.revenue.toFixed(2)}`,
        `$${seller.commission.toFixed(2)}`
      ])
      
      autoTable(doc, {
        head: [['Seller Name', 'Revenue', 'Commission']],
        body: sellersData,
        startY: yPosition,
        theme: 'striped',
        headStyles: {
          fillColor: [255, 102, 0]
        }
      })
    }
    
    // Footer on each page
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      )
    }
    
    // Save
    doc.save(`${fileName}.pdf`)
    
    return true
  } catch (error) {
    console.error('Error generating financial report PDF:', error)
    throw error
  }
}

/**
 * Generate seller analytics PDF
 */
export function generateSellerAnalyticsPDF(
  sellerData: SellerPerformance,
  fileName: string = 'seller_analytics'
) {
  try {
    const doc = new jsPDF()
    let yPosition = 20
    
    // Title
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text('Seller Performance Report', 105, yPosition, { align: 'center' })
    yPosition += 15
    
    // Seller Name
    doc.setFontSize(14)
    doc.text(sellerData.sellerName, 105, yPosition, { align: 'center' })
    yPosition += 10
    
    // Period
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    const startDate = sellerData.period.start.toLocaleDateString('en-US')
    const endDate = sellerData.period.end.toLocaleDateString('en-US')
    doc.text(`Period: ${startDate} - ${endDate}`, 14, yPosition)
    yPosition += 10
    
    doc.setFontSize(10)
    doc.text(`Generated: ${new Date().toLocaleString('en-US')}`, 14, yPosition)
    yPosition += 15
    
    // Performance Score
    doc.setFontSize(16)
    doc.setFont('helvetica', 'bold')
    const scoreColor = sellerData.performanceScore >= 80 ? [0, 150, 0] : 
                      sellerData.performanceScore >= 60 ? [200, 150, 0] : [200, 0, 0]
    doc.setTextColor(scoreColor[0], scoreColor[1], scoreColor[2])
    doc.text(`Performance Score: ${sellerData.performanceScore}/100`, 105, yPosition, { align: 'center' })
    doc.setTextColor(0, 0, 0)
    yPosition += 15
    
    // Key Metrics
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Key Performance Indicators', 14, yPosition)
    yPosition += 10
    
    const metricsData = [
      ['Total Offers Sent', sellerData.totalOffers.toString()],
      ['Accepted Offers', sellerData.acceptedOffers.toString()],
      ['Rejected Offers', sellerData.rejectedOffers.toString()],
      ['Acceptance Rate', `${sellerData.acceptanceRate.toFixed(1)}%`],
      ['Completed Orders', sellerData.completedOrders.toString()],
      ['Cancelled Orders', sellerData.cancelledOrders.toString()],
      ['Completion Rate', `${sellerData.completionRate.toFixed(1)}%`],
      ['Average Response Time', `${sellerData.averageResponseTime.toFixed(1)} minutes`],
      ['Average Rating', `${sellerData.averageRating.toFixed(2)} / 5.0`],
      ['Total Ratings', sellerData.totalRatings.toString()],
      ['Lost Deals', sellerData.lostDeals.toString()],
      ['Total Revenue', `$${sellerData.totalRevenue.toFixed(2)}`],
      ['Total Commission', `$${sellerData.totalCommission.toFixed(2)}`]
    ]
    
    autoTable(doc, {
      body: metricsData,
      startY: yPosition,
      theme: 'grid',
      styles: { fontSize: 10 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 90 },
        1: { halign: 'right', cellWidth: 60 }
      }
    })
    
    yPosition = (doc as any).lastAutoTable.finalY + 15
    
    // Lost Deals Reasons
    if (Object.keys(sellerData.lostDealsReasons).length > 0) {
      if (yPosition > 240) {
        doc.addPage()
        yPosition = 20
      }
      
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Lost Deals by Reason', 14, yPosition)
      yPosition += 10
      
      const lostDealsData = Object.entries(sellerData.lostDealsReasons).map(
        ([reason, count]) => [reason, count.toString()]
      )
      
      autoTable(doc, {
        head: [['Reason', 'Count']],
        body: lostDealsData,
        startY: yPosition,
        theme: 'striped',
        headStyles: {
          fillColor: [255, 102, 0]
        }
      })
    }
    
    // Footer
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      )
    }
    
    // Save
    doc.save(`${fileName}.pdf`)
    
    return true
  } catch (error) {
    console.error('Error generating seller analytics PDF:', error)
    throw error
  }
}

/**
 * Export multiple sheets to Excel
 */
export function exportMultipleSheetsToExcel(
  sheets: Array<{ name: string; data: any[] }>,
  fileName: string
) {
  try {
    const workbook = XLSX.utils.book_new()
    
    sheets.forEach(sheet => {
      if (sheet.data.length > 0) {
        const worksheet = XLSX.utils.json_to_sheet(sheet.data)
        XLSX.utils.book_append_sheet(workbook, worksheet, sheet.name)
      }
    })
    
    XLSX.writeFile(workbook, `${fileName}.xlsx`)
    
    return true
  } catch (error) {
    console.error('Error exporting multiple sheets to Excel:', error)
    throw error
  }
}

